/** Decode bounded UTF-8 lines without assuming transport chunks end at character or line boundaries. */
export async function* readTextStreamLines(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  limits = { line: 96_000, total: 4_000_000 },
): AsyncGenerator<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let pending = "";
  let total = 0;
  let skipLineFeed = false;
  const cancel = () => void reader.cancel().catch(() => {});
  signal.addEventListener("abort", cancel, { once: true });
  try {
    while (true) {
      signal.throwIfAborted();
      const { value, done } = await reader.read();
      signal.throwIfAborted();
      total += value?.byteLength ?? 0;
      if (total > limits.total) throw new Error("Stream too large");
      let decoded = decoder.decode(value, { stream: !done });
      // CR is a complete line ending itself; swallow a following LF even in the next chunk.
      if (skipLineFeed && decoded.length > 0) {
        if (decoded.startsWith("\n")) decoded = decoded.slice(1);
        skipLineFeed = false;
      }
      pending += decoded;
      let end: number;
      while ((end = pending.search(/[\r\n]/)) !== -1) {
        if (end > limits.line) throw new Error("Stream line too large");
        const carriageReturn = pending[end] === "\r";
        yield pending.slice(0, end);
        pending = pending.slice(end + 1);
        if (carriageReturn) {
          if (pending.startsWith("\n")) pending = pending.slice(1);
          else if (pending.length === 0) skipLineFeed = true;
        }
      }
      if (pending.length > limits.line) throw new Error("Stream line too large");
      if (done) {
        if (pending) yield pending;
        return;
      }
    }
  } finally {
    signal.removeEventListener("abort", cancel);
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
